// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused".
// The hub import warmed the Rematch model cluster; this specifier restores
// that init order. It retires with the Z2 codemod.
import '@/pages/api/scribeAPIV2';

import { type EHRProfileResponse } from '@/types';
import { shouldSuppressDesktopOfflineNetworkErrorToast } from '@/utils/http/desktopOfflineNetwork';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import {
  getEhrConnectionErrorMessage,
  isEhrConnectionError,
  useQueryUserEHR as useQueryUserEHRCore,
} from '@heidi/integrations/ehr/data';
import { useTranslations } from '@heidi/runtime/i18n';
import { AxiosError } from 'axios';
import { useQueryKindeProperties } from '../useQueryKindeProperties';
import { useQueryUserForContext } from '../useQueryUser';

export function useQueryUserEHR<T = EHRProfileResponse>(
  select?: (data: EHRProfileResponse) => T
) {
  const { t } = useTranslations();
  const { data: kindeProperties } = useQueryKindeProperties();
  const { data: user } = useQueryUserForContext((data) => data.kinde_user_id);

  return useQueryUserEHRCore({
    enabled: !!kindeProperties?.region && !!user,
    select,
    onUserEhrFetchError: (error) => {
      if (isEhrConnectionError(error)) {
        toast({
          tone: 'destructive',
          title: t('ehr.ehrConnectionError'),
          description: getEhrConnectionErrorMessage(error),
        });
      } else if (error instanceof AxiosError) {
        // Never toast the raw payload — EHR responses can carry PHI/PII.
        // The raw error goes to the log channel only; the toast shows a
        // fixed, safe message that never echoes backend detail strings.
        logger.error('User EHR request failed', error);
        if (!shouldSuppressDesktopOfflineNetworkErrorToast(error)) {
          toast({
            tone: 'destructive',
            title: t('ehr.ehrConnectionError'),
            description: t('ehr.pleaseTryAgain'),
          });
        }
      }
    },
  });
}
